import { useEffect, useState, useRef } from 'react';
import { stravaService } from '../lib/strava';
import { getStravaSettings, updateStravaSettings, clearStravaSettings, StravaSettings } from '../lib/stravaSettings';
import stravaLogo from '../assets/strava-logo.svg';
import stravaConnectButton from '../assets/btn_strava_connect_with_orange.svg';

interface StravaAuthProps {
  onAuthChange: (_authenticated: boolean) => void;
  onSettingsChange?: (_settings: StravaSettings) => void;
}

export function StravaAuth({ onAuthChange, onSettingsChange }: StravaAuthProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [athleteName, setAthleteName] = useState<string>('');
  const [profilePic, setProfilePic] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string>('');
  const [settings, setSettings] = useState<StravaSettings>(getStravaSettings());
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const codeProcessedRef = useRef(false);
  const settingsDropdownRef = useRef<HTMLDivElement>(null);

  // Check for existing authentication on component mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (error) {
      setError('Strava authorization was denied');
      setIsLoading(false);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (code && !codeProcessedRef.current) {
      codeProcessedRef.current = true;
      handleAuthorizationCode(code);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target as Node)) {
        setShowSettingsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      const hasValidTokens = await stravaService.hasValidTokens();

      if (hasValidTokens) {
        const accessToken = await stravaService.getAccessToken();
        if (accessToken) {
          try {
            // Allow failure during check - use cached data if available
            const athlete = await stravaService.getAthlete(accessToken, true);
            setIsAuthenticated(true);
            setAthleteName(athlete.firstname + ' ' + athlete.lastname);
            setProfilePic(athlete.profile_medium || '');
            onAuthChange(true);
          } catch (error) {
            console.warn(
              'Failed to get athlete data and no cache available:',
              error
            );
            // Clear tokens since we can't verify the connection
            stravaService.clearStoredTokens();
            setIsAuthenticated(false);
            onAuthChange(false);
          }
        } else {
          // Token refresh failed, clear auth state
          setIsAuthenticated(false);
          onAuthChange(false);
        }
      } else {
        setIsAuthenticated(false);
        onAuthChange(false);
      }
    } catch (err) {
      console.error('Error checking auth status:', err);
      setIsAuthenticated(false);
      onAuthChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthorizationCode = async (code: string) => {
    try {
      setIsConnecting(true);
      setError('');

      const tokens = await stravaService.exchangeCodeForTokens(code);
      stravaService.storeTokens(tokens);

      // Use athlete data from token response (already cached)
      if (tokens.athlete) {
        setIsAuthenticated(true);
        setAthleteName(
          tokens.athlete.firstname + ' ' + tokens.athlete.lastname
        );
        setProfilePic(tokens.athlete.profile_medium || '');
        onAuthChange(true);
      } else {
        throw new Error('No athlete data received from Strava');
      }

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      console.error('Error handling authorization code:', err);
      setError('Failed to connect to Strava. Please try again.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleConnect = () => {
    setIsConnecting(true);
    const authUrl = stravaService.getAuthorizationUrl();
    window.location.href = authUrl;
  };

  const handleDisconnect = () => {
    stravaService.clearStoredTokens();
    clearStravaSettings();
    setIsAuthenticated(false);
    setAthleteName('');
    setProfilePic('');
    setSettings(getStravaSettings()); // Reset to defaults
    onAuthChange(false);
    if (onSettingsChange) {
      onSettingsChange(getStravaSettings());
    }
  };

  const handleSettingChange = (key: keyof StravaSettings, value: boolean) => {
    const newSettings = updateStravaSettings({ [key]: value });
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white p-3 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
          <span className="text-sm text-gray-600">Checking Strava...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between">
        {isAuthenticated ? (
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2 pr-2">
              <img src={stravaLogo} alt="Strava" className="w-6 h-6" />
              {profilePic ? (
                <img
                  src={profilePic}
                  alt={athleteName}
                  className="w-6 h-6 rounded-full"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-600">
                    {athleteName
                      .split(' ')
                      .map((n) => n[0])
                      .join('')}
                  </span>
                </div>
              )}
              <span className="text-sm font-medium text-gray-900">
                {athleteName}
              </span>
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <button
              onClick={handleConnect}
              disabled={isConnecting}
              className="disabled:opacity-50 disabled:cursor-not-allowed transition-opacity hover:opacity-90"
            >
              {isConnecting ? (
                <div className="h-8 px-4 bg-orange-500 text-white rounded flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  <span className="text-xs">Connecting...</span>
                </div>
              ) : (
                <img
                  src={stravaConnectButton}
                  alt="Connect with Strava"
                  className="h-8"
                />
              )}
            </button>
            <span className="text-sm text-gray-600">
              to auto-upload workouts
            </span>
          </div>
        )}

        {isAuthenticated && (
          <div className="flex items-center space-x-2">
            {error && <span className="text-xs text-red-600">{error}</span>}
            
            {/* Settings Dropdown */}
            <div className="relative" ref={settingsDropdownRef}>
              <button
                onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                className="px-2 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors flex items-center"
                title="Strava upload settings"
              >
                ⚙️
              </button>
              
              {showSettingsDropdown && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-64 p-3">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Strava Upload Settings</h4>
                  
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.autoUpload}
                        onChange={(e) => handleSettingChange('autoUpload', e.target.checked)}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm text-gray-700">Auto-upload workouts</span>
                    </label>
                    
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.autoUploadPhoto}
                        onChange={(e) => handleSettingChange('autoUploadPhoto', e.target.checked)}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm text-gray-700">Auto-upload workout charts</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={handleDisconnect}
              className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              Disconnect
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
